import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, LinearProgress, useTheme, useMediaQuery, Tooltip, Chip } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Sector, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Area, AreaChart, LineChart, Line } from 'recharts';
type ChartType = 'donut' | 'radar' | 'area' | 'line' | 'progress' | 'stackedBar';
interface ChartCardProps {
  title: string;
  type: ChartType;
  data?: any;
  height?: number;
}
export const ChartCard: React.FC<ChartCardProps> = ({
  title,
  type,
  data = null,
  height = 240
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeIndex, setActiveIndex] = useState(0);
  const onPieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };
  // Donut chart rendering with recharts
  const renderDonutChart = () => {
    if (!data) return null;
    // Format data for recharts
    const chartData = data.labels.map((label: string, index: number) => ({
      name: label,
      value: data.values[index],
      count: data.counts[index]
    }));
    const renderActiveShape = (props: any) => {
      const {
        cx,
        cy,
        innerRadius,
        outerRadius,
        startAngle,
        endAngle,
        fill,
        payload
      } = props;
      return <g data-id="element-178">
          <text x={cx} y={cy - 10} dy={8} textAnchor="middle" fill={fill} fontSize={16} fontWeight="bold" data-id="element-179">
            {payload.name}
          </text>
          <text x={cx} y={cy + 15} textAnchor="middle" fill="#888" fontSize={14} data-id="element-180">
            {payload.count}
          </text>
          <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 5} startAngle={startAngle} endAngle={endAngle} fill={fill} data-id="element-181" />
        </g>;
    };
    return <Box sx={{
      height,
      width: '100%'
    }} data-id="element-182">
        <ResponsiveContainer width="100%" height="100%" data-id="element-183">
          <PieChart data-id="element-184">
            <Pie activeIndex={activeIndex} activeShape={renderActiveShape} data={chartData} cx="50%" cy="50%" innerRadius={isMobile ? 50 : 70} outerRadius={isMobile ? 70 : 90} dataKey="value" onMouseEnter={onPieEnter} paddingAngle={2} data-id="element-185">
              {chartData.map((entry: any, index: number) => <Cell key={`cell-${index}`} fill={data.colors[index]} stroke={data.colors[index]} strokeWidth={1} data-id="element-186" />)}
            </Pie>
            <Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{
            paddingTop: 12
          }} formatter={(value, entry, index) => {
            return <span style={{
              color: data.colors[index],
              fontWeight: activeIndex === index ? 'bold' : 'normal',
              fontSize: 12
            }} data-id="element-188">
                    {value} ({data.values[index]}%)
                  </span>;
          }} data-id="element-187" />
          </PieChart>
        </ResponsiveContainer>
      </Box>;
  };
  // Stacked Bar Chart rendering
  const renderStackedBarChart = () => {
    if (!data) return null;
    return <Box sx={{
      height,
      width: '100%'
    }} data-id="element-189">
        <ResponsiveContainer width="100%" height="100%" data-id="element-190">
          <BarChart data={data.categories.map((category: string, index: number) => {
          const entry: any = {
            name: category
          };
          data.series.forEach((s: any) => {
            entry[s.name] = s.data[index];
          });
          return entry;
        })} margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 5
        }} data-id="element-191">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-192" />
            <XAxis dataKey="name" axisLine={false} tickLine={false} data-id="element-193" />
            <YAxis axisLine={false} tickLine={false} data-id="element-194" />
            <RechartsTooltip formatter={(value, name) => [`${value}`, name]} labelFormatter={label => `Day: ${label}`} contentStyle={{
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            border: 'none'
          }} data-id="element-195" />
            <Legend wrapperStyle={{
            paddingTop: 15
          }} formatter={value => <span style={{
            fontSize: 14,
            color: '#333'
          }} data-id="element-197">
                  {value}
                </span>} data-id="element-196" />
            {data.series.map((s: any, index: number) => <Bar key={s.name} dataKey={s.name} stackId="a" fill={s.color} radius={[index === 0 && 4, index === 0 && 4, index === data.series.length - 1 && 4, index === data.series.length - 1 && 4]} data-id="element-198" />)}
          </BarChart>
        </ResponsiveContainer>
      </Box>;
  };
  // Area Chart rendering
  const renderAreaChart = () => {
    if (!data) return null;
    return <Box sx={{
      height,
      width: '100%'
    }} data-id="element-199">
        <ResponsiveContainer width="100%" height="100%" data-id="element-200">
          <AreaChart data={data.categories.map((category: string, index: number) => {
          const entry: any = {
            name: category
          };
          data.series.forEach((s: any) => {
            entry[s.name] = s.data[index];
          });
          return entry;
        })} margin={{
          top: 10,
          right: 30,
          left: 0,
          bottom: 0
        }} data-id="element-201">
            <defs data-id="element-202">
              <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1" data-id="element-203">
                <stop offset="5%" stopColor="#0052CC" stopOpacity={0.8} data-id="element-204" />
                <stop offset="95%" stopColor="#0052CC" stopOpacity={0.1} data-id="element-205" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-206" />
            <XAxis dataKey="name" axisLine={false} tickLine={false} data-id="element-207" />
            <YAxis axisLine={false} tickLine={false} data-id="element-208" />
            <RechartsTooltip formatter={(value, name) => [`${value}`, name]} contentStyle={{
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            border: 'none'
          }} data-id="element-209" />
            {data.series.map((s: any) => <Area key={s.name} type="monotone" dataKey={s.name} stroke="#0052CC" strokeWidth={3} fillOpacity={1} fill="url(#colorGradient)" data-id="element-210" />)}
          </AreaChart>
        </ResponsiveContainer>
      </Box>;
  };
  // Line Chart rendering
  const renderLineChart = () => {
    if (!data) return null;
    return <Box sx={{
      height,
      width: '100%'
    }} data-id="element-211">
        <ResponsiveContainer width="100%" height="100%" data-id="element-212">
          <LineChart data={data.categories.map((category: string, index: number) => {
          const entry: any = {
            name: category
          };
          data.series.forEach((s: any) => {
            entry[s.name] = s.data[index];
          });
          return entry;
        })} margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 5
        }} data-id="element-213">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-214" />
            <XAxis dataKey="name" axisLine={false} tickLine={false} data-id="element-215" />
            <YAxis axisLine={false} tickLine={false} data-id="element-216" />
            <RechartsTooltip formatter={(value, name) => [`${value}`, name]} contentStyle={{
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            border: 'none'
          }} data-id="element-217" />
            <Legend data-id="element-218" />
            {data.series.map((s: any) => <Line key={s.name} type="monotone" dataKey={s.name} stroke={s.color} strokeWidth={3} dot={{
            r: 4,
            strokeWidth: 2
          }} activeDot={{
            r: 6,
            strokeWidth: 0
          }} data-id="element-219" />)}
          </LineChart>
        </ResponsiveContainer>
      </Box>;
  };
  // Progress Chart rendering (existing implementation)
  const renderProgressChart = () => {
    return <Box sx={{
      height,
      p: 2
    }} data-id="element-220">
        <Box sx={{
        mb: 4
      }} data-id="element-221">
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 1
        }} data-id="element-222">
            <Typography variant="body2" data-id="element-223">KYC Completed</Typography>
            <Typography variant="body2" fontWeight="600" color="primary" data-id="element-224">
              78%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={78} sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: '#E5E7EB',
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            backgroundColor: '#0052CC'
          }
        }} data-id="element-225" />
        </Box>
        <Box sx={{
        mb: 4
      }} data-id="element-226">
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 1
        }} data-id="element-227">
            <Typography variant="body2" data-id="element-228">Cases in Review</Typography>
            <Typography variant="body2" fontWeight="600" color="primary" data-id="element-229">
              23%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={23} sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: '#E5E7EB',
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            backgroundColor: '#0052CC'
          }
        }} data-id="element-230" />
        </Box>
        <Box data-id="element-231">
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 1
        }} data-id="element-232">
            <Typography variant="body2" data-id="element-233">Rejected Cases</Typography>
            <Typography variant="body2" fontWeight="600" color="error" data-id="element-234">
              3%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={3} sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: '#FEF2F2',
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            backgroundColor: '#AE2A19'
          }
        }} data-id="element-235" />
        </Box>
      </Box>;
  };
  // Radar Chart rendering (placeholder implementation)
  const renderRadarChart = () => {
    return <Box sx={{
      height,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative'
    }} data-id="element-236">
        <svg width="200" height="200" viewBox="0 0 200 200" data-id="element-237">
          <polygon points="100,10 190,78 154,190 46,190 10,78" fill="rgba(0, 82, 204, 0.1)" stroke="#0052CC" strokeWidth="2" data-id="element-238" />
          <polygon points="100,40 160,88 134,160 66,160 40,88" fill="rgba(0, 82, 204, 0.2)" stroke="#0052CC" strokeWidth="2" data-id="element-239" />
          <line x1="100" y1="10" x2="100" y2="190" stroke="#E5E7EB" strokeWidth="1" data-id="element-240" />
          <line x1="10" y1="78" x2="190" y2="78" stroke="#E5E7EB" strokeWidth="1" data-id="element-241" />
          <line x1="46" y1="190" x2="154" y2="190" stroke="#E5E7EB" strokeWidth="1" data-id="element-242" />
          <circle cx="100" cy="10" r="4" fill="#0052CC" data-id="element-243" />
          <circle cx="190" cy="78" r="4" fill="#0052CC" data-id="element-244" />
          <circle cx="154" cy="190" r="4" fill="#0052CC" data-id="element-245" />
          <circle cx="46" cy="190" r="4" fill="#0052CC" data-id="element-246" />
          <circle cx="10" cy="78" r="4" fill="#0052CC" data-id="element-247" />
        </svg>
        <Box sx={{
        position: 'absolute',
        bottom: 10,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        gap: 2
      }} data-id="element-248">
          <Typography variant="caption" data-id="element-249">Coverage: 87%</Typography>
        </Box>
      </Box>;
  };
  // Render the appropriate chart based on type
  const renderChart = () => {
    switch (type) {
      case 'donut':
        return renderDonutChart();
      case 'stackedBar':
        return renderStackedBarChart();
      case 'area':
        return renderAreaChart();
      case 'line':
        return renderLineChart();
      case 'radar':
        return renderRadarChart();
      case 'progress':
        return renderProgressChart();
      default:
        return null;
    }
  };
  return <Card sx={{
    height: '100%',
    borderRadius: '8px',
    border: '1px solid #E5E7EB',
    boxShadow: '0px 1px 2px rgba(0,0,0,0.05)'
  }} data-id="element-250">
      <CardContent sx={{
      p: 3,
      height: '100%'
    }} data-id="element-251">
        <Typography variant="h6" component="h2" gutterBottom sx={{
        fontWeight: 700,
        color: '#1F2937',
        mb: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        fontSize: '1rem'
      }} data-id="element-252">
          {title}
        </Typography>
        {renderChart()}
      </CardContent>
    </Card>;
};
export default ChartCard;